import { useCallback, useMemo, useRef } from 'react';

import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import type { LC_ValidationError } from '@/api/shared';
import { useAttendanceMarksAdmin } from './useAttendanceMarksAdmin';
import type { UseAttendanceMarksAdminResult } from './useAttendanceMarksAdmin';
import { useAttendanceFiltersPanel } from '../common/useAttendanceFiltersPanel';
import type { UseAttendanceFiltersPanelResult } from '../common/useAttendanceFiltersPanel';
import type { SearchFilterConfig } from '../common/useAttendanceSearchFilters';
import type { DimensionalFilterConfig } from '../common/useEmployeeDimensionalFilter';


export interface UseAttendanceMarksAdminPageOptions {
  searchConfig: SearchFilterConfig;
  dimensionalConfig: DimensionalFilterConfig;
}

export interface UseAttendanceMarksAdminPageResult {
  marks: UseAttendanceMarksAdminResult;
  filtersPanel: UseAttendanceFiltersPanelResult;
}

export function useAttendanceMarksAdminPage(
  options: UseAttendanceMarksAdminPageOptions,
): UseAttendanceMarksAdminPageResult {
  const { searchConfig, dimensionalConfig } = options;
  const { showApiError } = useUI();
  const { t: translate } = useTranslation();

  // Ref to break the circular dependency: the marks hook needs the error injector
  // from the filters panel, but the panel needs onFiltersApply from the marks hook.
  const injectSearchErrorsRef =
    useRef<UseAttendanceFiltersPanelResult['injectSearchErrors'] | null>(null);

  const handleValidationErrors = useCallback((validationErrors: LC_ValidationError[]) => {
    const inject = injectSearchErrorsRef.current;
    if (!inject) return;

    const mappedCount = inject(validationErrors);
    // Only fall back to the global notification when no error mapped to a visible
    // field — avoids silent failures for page/orderBy/employeeIds/403/503 errors.
    if (mappedCount === 0) {
      showApiError(
        { success: false, error: { description: '', validationErrors } },
        translate('attendance:marksAdmin.messages.loadError'),
      );
    }
  }, [showApiError, translate]);

  const marks = useAttendanceMarksAdmin({ onValidationErrors: handleValidationErrors });

  const filtersPanelOptions = useMemo(() => ({
    dimensionalConfig,
    searchConfig,
    onSearch: marks.onFiltersApply,
  }), [dimensionalConfig, searchConfig, marks.onFiltersApply]);

  const filtersPanel = useAttendanceFiltersPanel(filtersPanelOptions);

  // Keep the ref in sync so handleValidationErrors always calls the latest injector.
  injectSearchErrorsRef.current = filtersPanel.injectSearchErrors;

  return { marks, filtersPanel };
}
